/**
 * A single row in the dock received shipments table. Shows details for a single shipment.
 */

import React, { useState, useCallback, useMemo } from "react";
import { TableRow, TableCell } from "@amzn/meridian/table";
import Row from "@amzn/meridian/row";
import Link from "@amzn/meridian/link";
import Text from "@amzn/meridian/text";
import Icon from "@amzn/meridian/icon";
import Button from "@amzn/meridian/button";
import Tooltip from "@amzn/meridian/tooltip"
import dockReceivedPropType from "../../prop-types/dock-received";
import { convertUnix } from "../../utilities/convert-unix";
import ExceptionTag from "../app/exception-tag";
import useWarehouseData from "../../hooks/use-warehouse-data";
import { getWarehouseAttributes } from "../../utilities/get-warehouse-attributes";
import { getTransshipmentLink, getVendorLink } from "../../utilities/links";
import NYRModal from "../dock-received/nyr/nyr-modal";
import NYSModal from "../dock-received/nys/nys-modal";
import { loadIdDisplayLength } from "../Constants";
import copyTokens from "@amzn/meridian-tokens/base/icon/copy";
import Toaster from "@amzn/meridian/toaster";
import Alert from "@amzn/meridian/alert";
import EventIcon from "../event/event-icon";
import { getSummaryQuantity } from "../../utilities/parent-child-conversion";

const NetworkLoadSlaTableRow = (props) => {
    const {
        dockReceived
    } = props;

    const warehouseParams = useWarehouseData();

    // Memoize warehouse attributes to avoid recalculation on every render
    const warehouseAttributes = useMemo(() => {
        return getWarehouseAttributes({
            warehouseId: dockReceived.warehouseId,
            warehouseParams: warehouseParams,
        });
    }, [dockReceived.warehouseId, warehouseParams]);

    const { region_id, time_zone, wms } = warehouseAttributes;


    // Memoize summary quantities to avoid recalculation
    const summaryQuantities = useMemo(() => ({
        confirmedUnits: getSummaryQuantity(dockReceived, 'confirmedUnits', 'childConfirmedUnits'),
        asrsConfirmedUnits: getSummaryQuantity(dockReceived, 'asrsConfirmedUnits', 'childAsrsConfirmedUnits'),
        receivedUnits: getSummaryQuantity(dockReceived, 'receivedUnits', 'childReceivedUnits'),
        asrsReceivedUnits: getSummaryQuantity(dockReceived, 'asrsReceivedUnits', 'childAsrsReceivedUnits'),
        nyrnys: getSummaryQuantity(dockReceived, 'nyrnys', 'childNyrnys')
    }), [dockReceived]);

    const [toasts, setToasts] = useState([]);
    const onCloseToast = useCallback(id => setToasts(toasts => toasts.filter(t => t.id !== id)), []);
    const onOpenToast = useCallback(loadId => setToasts(toasts => toasts.concat({ id: loadId, timeout: 1500 })), []);



    return (
        <TableRow key={dockReceived.id} rowComponents={props.rowComponents}>
            <Toaster toasts={toasts} onCloseToast={onCloseToast}>
                {toast => (
                    <Alert toast={true} onClose={toast.onClose}>
                        {`${toast.id} copied to clipboard`}
                    </Alert>
                )}
            </Toaster>
            <TableCell alignmentHorizontal="center">
                <Text tag="p">
                    {dockReceived.warehouseId}
                </Text>
            </TableCell>
            <TableCell alignmentHorizontal="center">
                <Text tag="p">
                    {dockReceived.loadType}
                </Text>
            </TableCell>
            <TableCell alignmentHorizontal="center">
                {dockReceived.loadType === 'Vendor' && dockReceived.distributorName ?
                    <Tooltip position="end" title={dockReceived.distributorName}>
                        <Text tag="p">
                            {dockReceived.source}
                        </Text>
                    </Tooltip>
                    :
                    <Text tag="p">
                        {dockReceived.source} - {dockReceived.vehicleRunId}
                    </Text>
                }
            </TableCell>

            <TableCell alignmentHorizontal="center" width={"10%"}>
                <Row alignmentHorizontal="center">
                    <Tooltip
                        position="end"
                        title={dockReceived.loadId}
                        id={`${dockReceived.id}-${dockReceived.loadId}-toolTip`}
                    >
                        <Link
                            href={dockReceived.loadType === "Vendor" ? getVendorLink(region_id, wms, dockReceived.loadId) : getTransshipmentLink(region_id, wms, dockReceived.loadId)}
                            target={"_blank"}
                            rel={"noopener noreferrer"}
                        >
                            <Text truncate={true}>{dockReceived.loadId.length > loadIdDisplayLength ? `${dockReceived.loadId.substring(0, loadIdDisplayLength)}...` : dockReceived.loadId}</Text>
                        </Link>
                    </Tooltip>
                    <Button type="icon" size="small" onClick={() => {
                        navigator.clipboard.writeText(dockReceived.loadId)
                        onOpenToast(dockReceived.loadId)
                    }
                    }>
                        <Icon tokens={copyTokens} />
                    </Button>
                </Row>
                {dockReceived.criticalAsinEvents &&
                    <Row alignmentHorizontal="center">
                        {dockReceived.criticalAsinEvents.map(criticalEvent => <EventIcon event={criticalEvent.eventName} regionId={region_id} />)}
                    </Row>
                }

                {dockReceived.suppression ?
                    <ExceptionTag exception={"suppressed"} />
                    : null
                }

            </TableCell>

            <TableCell alignmentHorizontal="center">
                <Text tag="p">
                    {(() => {
                        const slaDeadline = dockReceived.lastDockReceiveTime + ((dockReceived.sla ? Math.min(dockReceived.sla, 24) : 24) * 60 * 60 * 1000);
                        const timePastSLA = Date.now() - slaDeadline;
                        const hours = Math.floor(timePastSLA / (1000 * 60 * 60));
                        const minutes = Math.floor((timePastSLA % (1000 * 60 * 60)) / (1000 * 60));
                        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                    })()}
                </Text>
            </TableCell>

            <TableCell alignmentHorizontal="center">
                <Text tag="p">
                    {dockReceived.tempZone}
                </Text>
            </TableCell>

            <TableCell alignmentHorizontal="center">
                <Text tag="p">
                    {dockReceived.actualArrival ? convertUnix(dockReceived.actualArrival, time_zone) : ""}
                </Text>
            </TableCell>

            <TableCell alignmentHorizontal="center">
                <Text tag="p">
                    {dockReceived.dockReceived ? convertUnix(dockReceived.dockReceived, time_zone) : ""}
                </Text>
            </TableCell>

            <TableCell alignmentHorizontal="center">
                <Text tag="p">{summaryQuantities.confirmedUnits.toLocaleString()}</Text>
            </TableCell>

            <TableCell alignmentHorizontal="center">
                <Text tag="p">{summaryQuantities.asrsConfirmedUnits.toLocaleString()}</Text>
            </TableCell>

            <TableCell alignmentHorizontal="center">
                <Text tag="p">{`${dockReceived.asrsConfirmedUnitsPercent.toLocaleString()}%`}</Text>
            </TableCell>

            <TableCell alignmentHorizontal="center">
                <Text tag="p">{summaryQuantities.receivedUnits.toLocaleString()}</Text>
            </TableCell>

            <TableCell alignmentHorizontal="center">
                <Text tag="p">{summaryQuantities.asrsReceivedUnits.toLocaleString()}</Text>
            </TableCell>

            <TableCell alignmentHorizontal="center">
                {dockReceived.nyrnys > 0 ?
                    dockReceived.loadType === "Vendor" ?
                        <NYRModal
                            nyr={summaryQuantities.nyrnys}
                            po={dockReceived.loadId}
                            tempZone={dockReceived.tempZone}
                            criticalAsinEvents={dockReceived.criticalAsinEvents}
                            region_id={region_id}
                        />
                        : <NYSModal
                            nys={summaryQuantities.nyrnys}
                            manifestId={dockReceived.loadId}
                            tempZone={dockReceived.tempZone}
                            criticalAsinEvents={dockReceived.criticalAsinEvents}
                            region_id={region_id}
                        />

                    : <Text tag="p">{summaryQuantities.nyrnys.toLocaleString()}</Text>
                }
            </TableCell>

            <TableCell alignmentHorizontal="center">
                <Row>
                    <Text tag="p">
                        {`${dockReceived.progress}%`}
                    </Text>
                    {dockReceived.palletSummary &&
                        <Text>{`(${dockReceived.palletSummary.filter(shipment => shipment.received).length}/${dockReceived.palletSummary.length})`}</Text>
                    }
                </Row>
            </TableCell>

            <TableCell alignmentHorizontal="center">
                <Text tag="p">
                    {dockReceived.dwellDays}
                </Text>
            </TableCell>
        </TableRow>
    )
}

NetworkLoadSlaTableRow.propTypes = {
    dockReceived: dockReceivedPropType.isRequired,
    rowComponents: TableRow.propTypes.rowComponents,
}

export default React.memo(NetworkLoadSlaTableRow)
